import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueueConfig } from "../hooks/useQueueConfig";
import { useQueueState } from "../hooks/useQueueState";
import { useAuth } from "../context/AuthContext";
import { getStationDesk, setStationDesk, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { LogOut, Repeat, SkipForward, RotateCcw, Tv, Settings, Pencil, Megaphone } from "lucide-react";
import { toast } from "sonner";

const Operator = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { state, callNext, callManual, recall, skip, reset, configUpdatedAt } = useQueueState({ poll: true });
  const { config } = useQueueConfig({ refetchKey: configUpdatedAt });

  const [stationDesk, setStation] = useState(() => getStationDesk());
  const [showStationPicker, setShowStationPicker] = useState(() => !getStationDesk());
  const [tempStation, setTempStation] = useState(() => getStationDesk() || "");
  const [manualCode, setManualCode] = useState("");

  const nomen = config.nomenclature || "Guichê";
  const isOwner = user?.role === "owner";

  // Initialize tempStation when desks first arrive (only if empty)
  useEffect(() => {
    if (!tempStation && config.desks?.length) {
      setTempStation(config.desks[0]);
    }
  }, [config.desks, tempStation]);

  const confirmStation = () => {
    if (!tempStation) return;
    setStationDesk(tempStation);
    setStation(tempStation);
    setShowStationPicker(false);
    toast.success(`${nomen} ${tempStation} fixado nesta estação`);
  };

  const onCallPrefix = async (prefix) => {
    if (!stationDesk) return setShowStationPicker(true);
    try {
      const t = await callNext(prefix, stationDesk);
      toast.success(`Chamado ${t.code} → ${nomen} ${stationDesk}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Assinatura expirada — renove em Admin → Assinatura.");
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const onCallManual = async (e) => {
    e?.preventDefault();
    if (!stationDesk) return setShowStationPicker(true);
    const code = manualCode.trim();
    if (!code) {
      toast.error("Digite um código de senha");
      return;
    }
    try {
      const t = await callManual(code, stationDesk);
      toast.success(`Chamado ${t.code} → ${nomen} ${stationDesk}`);
      setManualCode("");
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Assinatura expirada — renove em Admin → Assinatura.");
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-foreground" />
            <span className="font-heading font-bold tracking-tighter text-lg">Console do Operador</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open("/tv", "_blank")} data-testid="operator-open-tv-btn" className="rounded-none">
              <Tv className="w-4 h-4 mr-2" /> TV
            </Button>
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="operator-admin-btn" className="rounded-none">
                <Settings className="w-4 h-4 mr-2" /> Admin
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} data-testid="operator-logout-btn" className="rounded-none">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="border border-border p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-3">Senha Atual</div>
            <div className="font-heading text-5xl font-black tracking-tighter" data-testid="operator-current-ticket">
              {state.currentTicket?.code || "—"}
            </div>
            <div className="text-sm text-foreground/60 mt-2">
              {state.currentTicket ? `${nomen} ${state.currentTicket.desk}` : "Nenhuma senha em chamada"}
            </div>
          </div>

          <div className="border border-foreground p-6 bg-foreground text-background relative">
            <div className="text-xs uppercase tracking-[0.25em] opacity-70 mb-3">Esta estação · {nomen}</div>
            <div className="font-heading text-5xl font-black tracking-tighter" data-testid="operator-station-desk">
              {stationDesk || "—"}
            </div>
            <button
              onClick={() => {
                setTempStation(stationDesk || config.desks[0]);
                setShowStationPicker(true);
              }}
              className="absolute top-4 right-4 p-2 hover:bg-background hover:text-foreground transition-colors"
              data-testid="operator-change-station-btn"
              title="Trocar estação"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <div className="text-xs opacity-70 mt-2">Fixo neste navegador</div>
          </div>

          <div className="border border-border p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-3">Histórico</div>
            <div className="flex flex-wrap gap-2">
              {state.history?.slice(0, 4).map((h, i) => (
                <span key={i} className="font-mono text-sm border border-border px-2 py-1">
                  {h.code} · {nomen.charAt(0)}{h.desk}
                </span>
              ))}
              {(!state.history || state.history.length === 0) && (
                <span className="text-sm text-foreground/40">Sem histórico</span>
              )}
            </div>
          </div>
        </div>

        <div className="border border-border p-6 md:p-8 mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Chamar Senha Digitada</div>
          <form onSubmit={onCallManual} className="flex flex-col md:flex-row gap-3">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Ex: A-205, Maria S., 42..."
              className="h-14 text-2xl font-heading font-bold tracking-tight rounded-none flex-1"
              data-testid="operator-manual-input"
              maxLength={64}
            />
            <Button
              type="submit"
              className="h-14 rounded-none text-base uppercase tracking-[0.2em] font-semibold md:px-10"
              data-testid="operator-manual-call-btn"
            >
              <Megaphone className="w-5 h-5 mr-3" /> Chamar
            </Button>
          </form>
          <p className="text-xs text-foreground/50 mt-3">
            Útil para chamar nomes ou senhas fora da sequência automática.
          </p>
        </div>

        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Chamar Próxima Senha (Automático)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {config.prefixes?.map((p) => (
              <button
                key={p}
                onClick={() => onCallPrefix(p)}
                className="border border-foreground bg-foreground text-background hover:bg-background hover:text-foreground transition-colors duration-200 p-8 md:p-10 flex flex-col items-start gap-3"
                data-testid={`operator-call-${p}-btn`}
              >
                <span className="text-xs uppercase tracking-[0.25em] opacity-70">Próxima · Prefixo</span>
                <span className="font-heading text-7xl md:text-8xl font-black tracking-tighter leading-none">{p}</span>
                <span className="text-xs uppercase tracking-[0.25em] opacity-70 mt-2">
                  Próximo: {p}-{state.nextNumberByPrefix?.[p] ?? 101}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button onClick={recall} variant="outline" className="h-16 rounded-none text-base uppercase tracking-[0.2em]" data-testid="operator-recall-btn">
            <Repeat className="w-5 h-5 mr-3" /> Rechamar
          </Button>
          <Button onClick={skip} variant="outline" className="h-16 rounded-none text-base uppercase tracking-[0.2em]" data-testid="operator-skip-btn">
            <SkipForward className="w-5 h-5 mr-3" /> Pular
          </Button>
          <Button
            onClick={() => {
              if (window.confirm("Resetar contadores e histórico? Esta ação não pode ser desfeita.")) {
                reset();
                toast.success("Estado resetado");
              }
            }}
            variant="destructive"
            className="h-16 rounded-none text-base uppercase tracking-[0.2em]"
            data-testid="operator-reset-btn"
          >
            <RotateCcw className="w-5 h-5 mr-3" /> Reset
          </Button>
        </div>
      </main>

      <Dialog open={showStationPicker} onOpenChange={(o) => {
        // Allow closing only when user has a confirmed station; never auto-reopen
        if (o === false && stationDesk) setShowStationPicker(false);
      }}>
        <DialogContent
          className="rounded-none max-w-md"
          data-testid="operator-station-dialog"
          aria-describedby="station-dialog-desc"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl font-black tracking-tighter">Escolha sua estação</DialogTitle>
          </DialogHeader>
          <div id="station-dialog-desc" className="py-4 space-y-4">
            <p className="text-sm text-foreground/70">
              Selecione o {nomen.toLowerCase()} desta estação. A escolha fica fixa neste navegador
              até você trocar manualmente.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em]">{nomen}</Label>
              <Select value={tempStation} onValueChange={setTempStation}>
                <SelectTrigger className="h-14 text-2xl font-heading font-bold tracking-tight rounded-none mt-2" data-testid="operator-station-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config.desks?.map((d) => (
                    <SelectItem key={d} value={d} data-testid={`operator-station-option-${d}`}>
                      {nomen} {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={confirmStation} className="rounded-none w-full h-12 uppercase tracking-[0.2em]" data-testid="operator-station-confirm-btn">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Operator;
